// Asset list for asset table controls
const ASSET_LIST = [
  { symbol: 'BTC', label: 'BTC' },
  { symbol: 'ETH', label: 'ETH' },
  { symbol: 'TQQQ', label: 'TQQQ' },
  { symbol: 'UGL', label: 'UGL' },
  { symbol: 'IBIT', label: 'IBIT' },
  { symbol: 'SOXL', label: 'SOXL' },
  { symbol: 'FAS', label: 'FAS' },
  { symbol: 'TMF', label: 'TMF' },
  { symbol: 'LABU', label: 'LABU' },
  { symbol: 'UCO', label: 'UCO' },
  { symbol: 'PAXG', label: 'PAXG' },
];
// Timeframe options for table display
const TIMEFRAME_LIST = [
  { value: '1d', label: '1d' },
  { value: '1h', label: '1h' },
];
// List of models for control checkboxes
const MODEL_LIST = [
  'gpt-premium',
  'Gemini-premium',
  'Claude-premium',
  'BIS',
  'Claude',
  'gpt',
  'Gemini',
];
// Inject model row color CSS classes for dashboard tables
const modelRowColorStyles = `
  .claude-primary { background-color: #e8f5e8 !important; }
  .claude-basic { background-color: #f4faf4 !important; }
  .gemini-primary { background-color: #fdf1db !important; }
  .gemini-basic-1 { background-color: #fef8ed !important; }
  .gemini-basic-2 { background-color: #fefcf6 !important; }
  .gpt-primary { background-color: #e1f5fe !important; }
  .gpt-basic { background-color: #f0faff !important; }
  .bis-primary { background-color: #f3e8fd !important; }
`;
// Model row color mapping (copied from Reports/Report/index.js)
const getModelRowClass = (model) => {
  if (!model) return '';
  const modelLower = model.toLowerCase();
  if (modelLower.includes('claude-premium')) return 'claude-primary';
  if (modelLower.includes('gemini-premium')) return 'gemini-primary';
  if (modelLower.includes('gpt-premium')) return 'gpt-primary';
  if (modelLower.includes('claude') && !modelLower.includes('premium')) return 'claude-basic';
  if (modelLower.includes('gemini') && !modelLower.includes('premium')) return 'gemini-basic';
  if (modelLower.includes('gpt') && !modelLower.includes('premium')) return 'gpt-basic';
  if (modelLower.includes('bis') || modelLower.includes('BIS')) return 'bis-primary';
  return '';
};
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardBody, CardHeader, Col, Row, Spinner, Alert, Badge } from 'reactstrap';
import SessionUrlGenerator from './SessionUrlGenerator';
import config from '../../config';
import { getAuthHeaders } from "../../helpers/api_helper";
import ETFScoresService from '../../services/etfScoresService';

const PredictionsDashboard = () => {
  // Detect clean mode from URL
  const searchParams = new URLSearchParams(window.location.search);
  const isCleanMode = searchParams.get('clean') === 'true';
          // Session bypass logic is now handled globally in AuthProtected
        // Asset table filter state
        const [assetFilter, setAssetFilter] = useState(() => {
          const initial = {};
          ASSET_LIST.forEach(a => { initial[a.symbol] = true; });
          return initial;
        });

        // Handler for asset checkbox toggle
        const handleAssetCheckbox = (symbol) => {
          setAssetFilter(prev => ({ ...prev, [symbol]: !prev[symbol] }));
        };
      // Timeframe filter state: which timeframes are visible
      const [timeframeFilter, setTimeframeFilter] = useState({ '1d': true, '1h': false });
      const handleTimeframeCheckbox = (tf) => {
        setTimeframeFilter(prev => ({ ...prev, [tf]: !prev[tf] }));
      };
      // Model filter state: which models are visible
      const [modelFilter, setModelFilter] = useState(() => {
        const initial = {};
        MODEL_LIST.forEach(m => { initial[m] = true; });
        return initial;
      });

      // Handler for checkbox toggle
      const handleModelCheckbox = (model) => {
        setModelFilter(prev => ({ ...prev, [model]: !prev[model] }));
      };
    // Inject model row color styles once
    React.useEffect(() => {
      if (!document.getElementById('model-row-color-styles')) {
        const style = document.createElement('style');
        style.id = 'model-row-color-styles';
        style.innerHTML = modelRowColorStyles;
        document.head.appendChild(style);
      }
    }, []);
  const [predictions, setPredictions] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [etfScores, setEtfScores] = useState([]);
  const [etfScoresLoading, setEtfScoresLoading] = useState(false);

  const symbols = ['BTC', 'ETH', 'TQQQ', 'UGL', 'IBIT', 'SOXL', 'FAS', 'TMF', 'LABU', 'UCO', 'PAXG'];
  const timeframes = ['1d', '4h'];

  useEffect(() => {
    const fetchPredictions = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch predictions for each symbol
        const predictionsData = {};
        const allErrors = [];

        for (const symbol of symbols) {
          try {
            const params = new URLSearchParams();
            params.append('symbol', symbol);
            params.append('limit', '50');
            params.append('offset', '0');

            // Identify the caller: /api/predictions returns system forecasts only
            // to entitled members, and an unauthenticated request reads as free.
            const response = await fetch(`${config.api.API_URL}/api/predictions?${params}`, {
              credentials: 'include',
              headers: getAuthHeaders(),
            });

            if (response.ok) {
              const data = await response.json();
              const preds = data.predictions || [];

              // Filter: last 7 days of predictions (to include recent data from multiple days)
              const now = new Date();
              const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
              const sevenDaysAgo = new Date(today);
              sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

              const byDate = (p) => {
                if (!p.input_time) return false;
                const predDate = new Date(p.input_time);
                const predDay = new Date(predDate.getFullYear(), predDate.getMonth(), predDate.getDate());
                return predDay >= sevenDaysAgo && predDay <= today;
              };

              const sorted1d = preds
                .filter(p => p.timeframe === '1d')
                .filter(byDate)
                .sort((a, b) => new Date(b.input_time) - new Date(a.input_time));

              const sorted1h = preds
                .filter(p => p.timeframe === '1h')
                .filter(byDate)
                .sort((a, b) => new Date(b.input_time) - new Date(a.input_time));

              predictionsData[symbol] = { '1d': sorted1d, '1h': sorted1h };
            } else {
              allErrors.push(`Could not fetch ${symbol} predictions`);
            }
          } catch (err) {
            console.error(`Error fetching ${symbol} predictions:`, err);
            allErrors.push(`Error loading ${symbol}`);
          }
        }

        setPredictions(predictionsData);

        if (allErrors.length > 0) {
          console.warn('Had some errors loading predictions:', allErrors);
        }
      } catch (err) {
        console.error('Error in fetchPredictions:', err);
        setError('Failed to load predictions. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchPredictions();
  }, []);

  // Fetch ETF scores
  useEffect(() => {
    const fetchETFScores = async () => {
      try {
        setEtfScoresLoading(true);
        const result = await ETFScoresService.fetchETFScores();
        console.log('📊 ETF Scores result:', result);
        
        // Calculate scores from raw data
        if (result.rawData && Object.keys(result.rawData).length > 0) {
          const calculatedScores = ETFScoresService.calculateScoresLocally(result.rawData);
          console.log('📊 Calculated scores:', calculatedScores);
          setEtfScores(calculatedScores);
        } else {
          console.warn('⚠️ No raw data received for ETF scores');
          setEtfScores([]);
        }
      } catch (err) {
        console.error('❌ Error fetching ETF scores:', err);
        setEtfScores([]);
      } finally {
        setEtfScoresLoading(false);
      }
    };

    fetchETFScores();
  }, []);

  const buildReportUrl = (symbol, timeframe) => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const formatDate = (date) => date.toISOString().split('T')[0];
    const from = formatDate(yesterday);
    const to = formatDate(today);

    // Default models for report
    const models = 'gpt-premium%2BGemini-premium%2BClaude-premium%2BBIS%2BClaude%2Bgpt%2BGemini';

    return `/report?symbol=${symbol}&from=${from}&to=${to}&timeframe=${timeframe}&model=${models}`;
  };

  const getSuggestion = (prediction) => {
    // Derive suggestion based on the midpoint bias of the predicted range
    const currentPrice = parseFloat(prediction.current_price) || 0;
    const predictedHigh = parseFloat(prediction.prediction_high) || 0;
    const predictedLow = parseFloat(prediction.prediction_low) || 0;
    
    if (currentPrice <= 0 || (predictedHigh <= 0 && predictedLow <= 0)) {
      return 'CASH';
    }
    
    // Calculate percentage changes
    const highPct = ((predictedHigh - currentPrice) / currentPrice) * 100;
    const lowPct = ((predictedLow - currentPrice) / currentPrice) * 100;
    
    // Calculate midpoint bias of the range
    const midpointBias = (highPct + lowPct) / 2;
    
    // 0.3% threshold for 1h predictions, 0.5% for all others
    const threshold = prediction.timeframe === '1h' ? 0.3 : 0.5;
    if (midpointBias > threshold) {
      return 'LONG';
    }
    
    if (midpointBias < -threshold) {
      return 'SHORT';
    }
    
    return 'CASH';
  };

  const getSuggestionBadgeColor = (suggestion) => {
    if (suggestion === 'SHORT') return 'danger';
    if (suggestion === 'LONG') return 'success';
    return 'warning'; // CASH
  };

  const getModelBadgeColor = (confidence) => {
    if (!confidence) return 'secondary';
    if (confidence >= 75) return 'success';
    if (confidence >= 50) return 'warning';
    return 'danger';
  };

  const getPredictionStatus = (prediction, previousPrediction) => {
    if (!prediction.actual_price) return '';
    
    const currentPrice = parseFloat(prediction.current_price) || 0;
    const actualPrice = parseFloat(prediction.actual_price) || 0;
    if (currentPrice <= 0) return '';
    const direction = getSuggestion(prediction);
    const percentChange = ((actualPrice - currentPrice) / currentPrice) * 100;
    
    // Determine if there's a direction change
    let fee = 0.2; // Default fee for direction change
    if (previousPrediction) {
      const prevDirection = getSuggestion(previousPrediction);
      if (prevDirection === direction && direction !== 'CASH') {
        fee = 0; // No fee if same direction (holding position)
      }
    }
    
    if (direction === 'CASH') return '';
    if (direction === 'LONG') {
      if (percentChange > fee) return `Win (${percentChange.toFixed(2)}%, fee: -${fee}%)`;
      else return `Loss (${percentChange.toFixed(2)}%, fee: -${fee}%)`;
    } else if (direction === 'SHORT') {
      if (percentChange < -fee) return `Win (${percentChange.toFixed(2)}%, fee: -${fee}%)`;
      else return `Loss (${percentChange.toFixed(2)}%, fee: -${fee}%)`;
    }
    return '';
  };

  const getAccuracyValue = (prediction, period) => {
    // Map period to column name - use P/L (profit/loss) columns
    const columnMap = {
      '3d': '3dpl',
      '7d': '7dpl',
      '30d': '30dpl',
      '90d': '90dpl'
    };
    const col = columnMap[period];
    const value = prediction[col];
    if (value === null || value === undefined || value === '') return '-';
    // Parse the value as a float
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return '-';
    // P/L values are already in percentage format (e.g., 4.81), return with one decimal
    return `${numValue > 0 ? '+' : ''}${numValue.toFixed(1)}`;
  };

  const getWinRateValue = (prediction, period) => {
    // Map period to column name - use WR (win rate) columns
    const columnMap = {
      '3d': '3dwr',
      '7d': '7dwr',
      '30d': '30dwr',
      '90d': '90dwr'
    };
    const col = columnMap[period];
    const value = prediction[col];
    if (value === null || value === undefined || value === '') return '-';
    // Parse the value as a float
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return '-';
    // Win rate is 0-100, display as one decimal
    return `${numValue.toFixed(1)}`;
  };

  const getPullBackValue = (prediction, period) => {
    // Map period to column name - use PB (pull back) columns
    const columnMap = {
      '3d': '3dpb',
      '7d': '7dpb',
      '30d': '30dpb',
      '90d': '90dpb'
    };
    const col = columnMap[period];
    const value = prediction[col];
    if (value === null || value === undefined || value === '') return '-';
    // Parse the value as a float
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return '-';
    // Pull back is a percentage, display with sign and one decimal
    return `${numValue > 0 ? '+' : ''}${numValue.toFixed(1)}`;
  };

  const getPercentageRange = (prediction) => {
    const currentPrice = parseFloat(prediction.current_price) || 0;
    if (currentPrice === 0) return 'N/A';
    
    const low = parseFloat(prediction.prediction_low) || 0;
    const high = parseFloat(prediction.prediction_high) || 0;
    
    if (low === 0 && high === 0) return 'N/A';
    
    const lowPct = ((low - currentPrice) / currentPrice) * 100;
    const highPct = ((high - currentPrice) / currentPrice) * 100;
    
    return `${lowPct > 0 ? '+' : ''}${lowPct.toFixed(1)}% - ${highPct > 0 ? '+' : ''}${highPct.toFixed(1)}%`;
  };

  const getActualPercentageRange = (prediction) => {
    const currentPrice = parseFloat(prediction.current_price) || 0;
    if (currentPrice === 0) return '-';
    
    const actualLow = parseFloat(prediction.actual_low);
    const actualHigh = parseFloat(prediction.actual_high);
    const actualPrice = parseFloat(prediction.actual_price);
    
    // If we have actual range, show it
    if (!isNaN(actualLow) && !isNaN(actualHigh)) {
      const lowPct = ((actualLow - currentPrice) / currentPrice) * 100;
      const highPct = ((actualHigh - currentPrice) / currentPrice) * 100;
      return `${lowPct > 0 ? '+' : ''}${lowPct.toFixed(1)}% - ${highPct > 0 ? '+' : ''}${highPct.toFixed(1)}%`;
    }
    
    // If we have actual price, show it as a single point
    if (!isNaN(actualPrice)) {
      const pct = ((actualPrice - currentPrice) / currentPrice) * 100;
      return `${pct > 0 ? '+' : ''}${pct.toFixed(1)}%`;
    }
    
    // No actual price data yet
    return '-';
  };

  const getActualChangePercent = (prediction) => {
    const currentPrice = parseFloat(prediction.current_price);
    const actualPrice = parseFloat(prediction.actual_price);
    
    if (!currentPrice || currentPrice === 0 || isNaN(currentPrice) || isNaN(actualPrice)) {
      return '-';
    }
    
    const pct = ((actualPrice - currentPrice) / currentPrice) * 100;
    const color = pct > 0 ? 'success' : pct < 0 ? 'danger' : 'dark';
    return { value: `${pct > 0 ? '+' : ''}${pct.toFixed(2)}%`, color };
  };

  const formatCreatedDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric'
    });
  };

  const formatCreatedDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const month = date.toLocaleString('en-US', { month: 'short' });
    const day = date.getDate();
    const hh = String(date.getHours()).padStart(2, '0');
    const mm = String(date.getMinutes()).padStart(2, '0');
    return `${month} ${day}, ${hh}:${mm}`;
  };

  // Helper to append clean, timestamp, session params to a URL
  const appendSessionParams = (url) => {
    const searchParams = new URLSearchParams(window.location.search);
    const clean = searchParams.get('clean');
    const timestamp = searchParams.get('timestamp');
    const session = searchParams.get('session');
    const showreason = searchParams.get('showreason');
    const params = [];
    if (clean) params.push(`clean=${encodeURIComponent(clean)}`);
    if (timestamp) params.push(`timestamp=${encodeURIComponent(timestamp)}`);
    if (session) params.push(`session=${encodeURIComponent(session)}`);
    // default to showreason when coming from clean+session page (e.g. index)
    if (showreason === 'true' || (clean && session)) {
      params.push('showreason=true');
    }
    if (params.length === 0) return url;
    return url + (url.includes('?') ? '&' : '?') + params.join('&');
  };

  const getAssetName = (symbol) => {
    if (symbol === 'BTC') return 'Bitcoin';
    if (symbol === 'ETH') return 'Ethereum';
    if (symbol === 'PAXG') return 'PAX Gold';
    return `${symbol} ETF`;
  };

  const renderAssetSection = (symbol, timeframe = '1d') => {
    const asset = getAssetName(symbol);
    const symbolPredictions = ((predictions[symbol] || {})[timeframe] || [])
      .filter(pred => {
      if (!pred.model) return true;
      const modelLower = (pred.model || '').toLowerCase();
      // Special handling for gpt and gpt-premium
      if (modelLower.includes('gpt')) {
        return modelFilter[modelLower.includes('premium') ? 'gpt-premium' : 'gpt'];
      }
      if (modelLower.includes('gpt-premium')) {
        return modelFilter['gpt-premium'];
      }
      // Special handling for Claude models
      if (modelLower.includes('claude-premium') || (modelLower.includes('claude') && !modelLower.includes('premium'))) {
        return modelFilter[modelLower.includes('claude-premium') ? 'Claude-premium' : 'Claude'];
      }
      if (modelLower.includes('gpt-premium') || (modelLower.includes('gpt') && !modelLower.includes('premium'))) {
        return modelFilter[modelLower.includes('gpt-premium') ? 'gpt-premium' : 'gpt'];
      }
      if (modelLower.includes('gemini-premium') || (modelLower.includes('gemini') && !modelLower.includes('premium'))) {
        return modelFilter[modelLower.includes('gemini-premium') ? 'Gemini-premium' : 'Gemini'];
      }
      // Standard matching for other models
      const modelKey = MODEL_LIST.find(m => modelLower.includes(m.toLowerCase()));
      if (!modelKey) return true;
      return modelFilter[modelKey];
    })
    .sort((a, b) => new Date(b.input_time) - new Date(a.input_time));

    return (
      <div key={`${symbol}-${timeframe}`} className="mb-4">
        <Card>
          <CardHeader className="align-items-center d-flex">
            <div className="flex-grow-1">
              <Link
                to={buildReportUrl(symbol, timeframe)}
                className="card-title mb-0 link-primary"
                target="_blank"
              >
                <h4 className="card-title mb-0">
                  {timeframe === '1h' ? `${symbol} Predictions 1h - ${asset}` : `${symbol} Predictions - ${asset}`}
                </h4>
              </Link>
              <p className="text-muted fs-14 mb-0 mt-1">
                Latest predictions and model performance metrics
              </p>
            </div>
            <div className="flex-shrink-0">
              <Link
                to={buildReportUrl(symbol, timeframe)}
                className="btn btn-soft-info btn-sm"
                target="_blank"
              >
                <i className="ri-file-list-3-line align-middle"></i> View Report
              </Link>
            </div>
          </CardHeader>

          <CardBody>
            {loading ? (
              <div className="text-center py-4">
                <Spinner color="primary" size="sm" />
                <p className="mt-2">Loading predictions...</p>
              </div>
            ) : symbolPredictions.length === 0 ? (
              <Alert color="info">
                No predictions available for {symbol} yet. Check back soon!
              </Alert>
            ) : (
              <div className="table-responsive table-card">
                <table className="table table-borderless table-centered align-middle table-nowrap mb-0">
                  <thead className="text-muted table-light">
                    <tr>
                      <th scope="col"></th>
                      <th scope="col">Model</th>
                      <th scope="col">Version</th>
                      <th scope="col" style={{ width: '80px' }}>Dir</th>
                      <th scope="col" style={{ width: '90px' }}>Conf</th>
                      <th scope="col">Status</th>
                      <th scope="col">Created (UTC)</th>
                      <th scope="col">Predicted Range</th>
                      <th scope="col">Act Range</th>
                      <th scope="col">Curr Price</th>
                      <th scope="col">Act Price</th>
                      <th scope="col">Act Δ%</th>
                      <th scope="col">3d PL</th>
                      <th scope="col">3d WR</th>
                      <th scope="col">3d PB</th>
                      <th scope="col" style={{ borderLeftWidth: '2px', borderLeftStyle: 'solid', borderLeftColor: '#dee2e6' }}>7d PL</th>
                      <th scope="col">7d WR</th>
                      <th scope="col">7d PB</th>
                      <th scope="col" style={{ borderLeftWidth: '2px', borderLeftStyle: 'solid', borderLeftColor: '#dee2e6' }}>30d PL</th>
                      <th scope="col">30d WR</th>
                      <th scope="col">30d PB</th>
                      <th scope="col" style={{ borderLeftWidth: '2px', borderLeftStyle: 'solid', borderLeftColor: '#dee2e6' }}>90d PL</th>
                      <th scope="col">90d WR</th>
                      <th scope="col">90d PB</th>
                    </tr>
                  </thead>
                  <tbody>
                    {symbolPredictions.slice(0, 15).map((pred, index) => (
                      <tr key={pred.id} className={getModelRowClass(pred.model)}>
                        <td>
                          <Link
                            to={appendSessionParams(`/prediction/${pred.id}`)}
                            className="btn btn-soft-primary btn-sm"
                            target="_blank"
                          >
                            <i className="ri-external-link-line align-middle"></i>
                          </Link>
                        </td>
                        <td>
                          <span className="fw-medium fs-13">{pred.model || 'N/A'}</span>
                        </td>
                        <td className="fs-13">
                          <span className="text-muted fs-12">{pred.version || '-'}</span>
                        </td>
                        <td className="fs-13">
                          <span className={`text-${getSuggestionBadgeColor(getSuggestion(pred))} fw-medium`}>
                            {getSuggestion(pred)}
                          </span>
                        </td>
                        <td style={{ width: '90px' }}>
                          <span className={`text-${getModelBadgeColor(pred.confidence)} fw-medium`}>
                            {pred.confidence ? `${Math.round(pred.confidence)}` : 'N/A'}
                          </span>
                        </td>
                        <td>
                          {getPredictionStatus(pred, symbolPredictions[index + 1]) && (
                            <span className={`fw-bold text-${getPredictionStatus(pred, symbolPredictions[index + 1]).startsWith('Win') ? 'success' : getPredictionStatus(pred, symbolPredictions[index + 1]).startsWith('Loss') ? 'danger' : ''}`}>
                              {getPredictionStatus(pred, symbolPredictions[index + 1])}
                            </span>
                          )}
                        </td>
                        <td className="fs-13">
                          <span className="text-muted">
                            {formatCreatedDateTime(pred.input_time)}
                          </span>
                        </td>
                        <td className="fs-13">
                          <span className="text-primary fw-medium">{getPercentageRange(pred)}</span>
                        </td>
                        <td className="fs-13">
                          <span className={getActualPercentageRange(pred) === '-' ? 'text-muted' : 'text-info'}>
                            {getActualPercentageRange(pred)}
                          </span>
                        </td>
                        <td className="fs-13">
                          <span className="text-dark">{pred.current_price !== undefined && pred.current_price !== null ? Number(pred.current_price).toFixed(2) : '-'}</span>
                        </td>
                        <td className="fs-13">
                          <span className="text-dark">{pred.actual_price !== undefined && pred.actual_price !== null ? Number(pred.actual_price).toFixed(2) : '-'}</span>
                        </td>
                        <td className="fs-13">
                          {(() => {
                            const result = getActualChangePercent(pred);
                            return result === '-' ? <span className="text-muted">-</span> : <span className={`text-${result.color} fw-medium`}>{result.value}</span>;
                          })()}
                        </td>
                        <td className="fs-13">{getAccuracyValue(pred, '3d')}</td>
                        <td className="fs-13">{getWinRateValue(pred, '3d')}</td>
                        <td className="fs-13">{getPullBackValue(pred, '3d')}</td>
                        <td className="fs-13" style={{ borderLeftWidth: '2px', borderLeftStyle: 'solid', borderLeftColor: '#dee2e6' }}>{getAccuracyValue(pred, '7d')}</td>
                        <td className="fs-13">{getWinRateValue(pred, '7d')}</td>
                        <td className="fs-13">{getPullBackValue(pred, '7d')}</td>
                        <td className="fs-13" style={{ borderLeftWidth: '2px', borderLeftStyle: 'solid', borderLeftColor: '#dee2e6' }}>{getAccuracyValue(pred, '30d')}</td>
                        <td className="fs-13">{getWinRateValue(pred, '30d')}</td>
                        <td className="fs-13">{getPullBackValue(pred, '30d')}</td>
                        <td className="fs-13" style={{ borderLeftWidth: '2px', borderLeftStyle: 'solid', borderLeftColor: '#dee2e6' }}>{getAccuracyValue(pred, '90d')}</td>
                        <td className="fs-13">{getWinRateValue(pred, '90d')}</td>
                        <td className="fs-13">{getPullBackValue(pred, '90d')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardBody>
        </Card>
      </div>
    );
  };

  const renderETFScoresTable = () => {
    console.log('📊 renderETFScoresTable: loading=', etfScoresLoading, 'scores=', etfScores?.length || 0);

    if (etfScoresLoading) {
      return (
        <div className="mb-3" style={{ marginTop: '16px', padding: '12px', background: '#fef3c7', borderRadius: '6px', border: '1px solid #f59e0b' }}>
          <h4 style={{ margin: 0, fontSize: '13px', fontWeight: 600, color: '#111827' }}>ETF Prediction Scores (Timeframe 1d)</h4>
          <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>Loading ETF scores...</div>
        </div>
      );
    }

    return (
      <div className="mb-3" style={{ marginTop: '16px', padding: '12px', background: '#fef3c7', borderRadius: '6px', border: '1px solid #f59e0b' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <h4 style={{ margin: 0, fontSize: '13px', fontWeight: 600, color: '#111827' }}>ETF Prediction Scores (Timeframe 1d)</h4>
          <span style={{ fontSize: '11px', color: '#6b7280' }}>Last refreshed: {new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true })}</span>
        </div>
        <p style={{ margin: '0 0 8px 0', fontSize: '11px', color: '#6b7280' }}>
          <strong>Execution (3d/7d):</strong> S_base = Conf% × [(WR₃d × PL₃d) × 0.8 + (WR₇d × PL₇d) × 0.2] × PB_adj <span style={{ color: '#059669', fontSize: '11px' }}>v2.0 (更快响应)</span>
          <br /><strong>Confirmation (30d):</strong> F₃₀d adjusts S_base based on trend alignment (1.25 aligned / 0.75 contra / 1.0 neutral)
          <br /><strong>Environment (90d):</strong> Position multiplier based on market regime (Bull/Bear/Broken)
          <br /><strong>Strategy (90d):</strong> F₉₀d = 1.5 (resonance) / 1.0 (neutral) / 0.5 (conflict) - Ultimate big-picture filter
        </p>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '1px solid #f59e0b', background: '#fffbeb' }}>
                <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600 }}>ETF</th>
                <th style={{ padding: '8px', textAlign: 'center', fontWeight: 600 }}>Dir</th>
                <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, fontSize: '10px' }}>Valid Until (UTC)</th>
                <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600 }}>Conf%</th>
                <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, borderLeft: '1px solid #cbd5e1' }}>WR₃d</th>
                <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600 }}>PL₃d</th>
                <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600 }}>WR₇d</th>
                <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, borderRight: '1px solid #cbd5e1' }}>PL₇d</th>
                <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, borderLeft: '1px solid #cbd5e1' }}>WR₃₀d</th>
                <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600 }}>PL₃₀d</th>
                <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, borderRight: '1px solid #cbd5e1' }}>F₃₀d</th>
                <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, borderLeft: '1px solid #cbd5e1' }}>WR₉₀d</th>
                <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600 }}>PL₉₀d</th>
                <th style={{ padding: '8px', textAlign: 'center', fontWeight: 600, borderRight: '1px solid #cbd5e1' }}>Regime</th>
                <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, borderLeft: '1px solid #cbd5e1' }}>F₉₀d</th>
                <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, background: '#fef3c7', borderLeft: '1px solid #f59e0b' }}>Score</th>
              </tr>
            </thead>
            <tbody style={{ borderCollapse: 'collapse' }}>
              {etfScores && etfScores.length > 0 ? (
                etfScores.map((s, idx) => (
                  <tr key={idx} style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '6px 8px', fontWeight: 600 }}>{s.symbol}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'center', color: s.dir === 'LONG' ? '#10b981' : s.dir === 'SHORT' ? '#ef4444' : '#6b7280' }}>{s.dir}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', fontSize: '10px' }}>
                      {s.validUntilUTC 
                        ? new Date(s.validUntilUTC).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false })
                        : '-'
                      }
                    </td>
                    <td style={{ padding: '6px 8px', textAlign: 'right' }}>{(s.conf || 0).toFixed(1)}%</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right' }}>{(s.wr_3d || 0).toFixed(1)}%</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', color: (s.pl_3d || 0) >= 0 ? '#10b981' : '#ef4444' }}>{(s.pl_3d || 0) >= 0 ? '+' : ''}{(s.pl_3d || 0).toFixed(1)}%</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right' }}>{(s.wr_7d || 0).toFixed(1)}%</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', color: (s.pl_7d || 0) >= 0 ? '#10b981' : '#ef4444' }}>{(s.pl_7d || 0) >= 0 ? '+' : ''}{(s.pl_7d || 0).toFixed(1)}%</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right' }}>{(s.wr_30d || 0).toFixed(1)}%</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', color: (s.pl_30d || 0) >= 0 ? '#10b981' : '#ef4444' }}>{(s.pl_30d || 0) >= 0 ? '+' : ''}{(s.pl_30d || 0).toFixed(1)}%</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right' }}>{(s.f_30d || 1).toFixed(2)}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right' }}>{(s.wr_90d || 0).toFixed(1)}%</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', color: (s.pl_90d || 0) >= 0 ? '#10b981' : '#ef4444' }}>{(s.pl_90d || 0) >= 0 ? '+' : ''}{(s.pl_90d || 0).toFixed(1)}%</td>
                    <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                      {s.regime === 'bull' ? '📈 Bull' : s.regime === 'bear' ? '📉 Bear' : 'CASH'}
                    </td>
                    <td style={{ padding: '6px 8px', textAlign: 'right' }}>{(s.f_90d || 1).toFixed(2)}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 600, background: '#fef3c7', color: (s.score || 0) > 100 ? '#10b981' : (s.score || 0) < -100 ? '#ef4444' : '#6b7280' }}>{(s.score || 0) >= 0 ? '+' : ''}{(s.score || 0).toFixed(0)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="16" style={{ padding: '12px', textAlign: 'center', color: '#6b7280' }}>
                    No ETF data available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <React.Fragment>
      {/* Only show controls if not in clean mode */}
      {!isCleanMode && (
        <div className="mb-3" style={{ background: '#f8f9fa', borderRadius: 8, padding: 16, border: '1px solid #e9ecef' }}>
          <div className="d-flex align-items-center flex-wrap gap-3 mb-2">
            <span className="fw-bold me-2">Show Models:</span>
            {MODEL_LIST.map(model => (
              <label key={model} className="form-check-label me-3" style={{ userSelect: 'none', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  className="form-check-input me-1"
                  checked={modelFilter[model]}
                  onChange={() => handleModelCheckbox(model)}
                />
                {model}
              </label>
            ))}
          </div>
          {/* Asset filter controls */}
          <div className="d-flex align-items-center flex-wrap gap-3 mb-2">
            <span className="fw-bold me-2">Show Assets:</span>
            {ASSET_LIST.map(asset => (
              <label key={asset.symbol} className="form-check-label me-3" style={{ userSelect: 'none', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  className="form-check-input me-1"
                  checked={assetFilter[asset.symbol]}
                  onChange={() => handleAssetCheckbox(asset.symbol)}
                />
                {asset.label}
              </label>
            ))}
          </div>
          {/* Timeframe filter controls */}
          <div className="d-flex align-items-center flex-wrap gap-3 mb-2">
            <span className="fw-bold me-2">Show Timeframe:</span>
            {TIMEFRAME_LIST.map(tf => (
              <label key={tf.value} className="form-check-label me-3" style={{ userSelect: 'none', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  className="form-check-input me-1"
                  checked={timeframeFilter[tf.value]}
                  onChange={() => handleTimeframeCheckbox(tf.value)}
                />
                {tf.label}
              </label>
            ))}
          </div>
          {/* Session URL generator */}
          <SessionUrlGenerator />
        </div>
      )}
      {error && <Alert color="danger">{error}</Alert>}
      {renderETFScoresTable()}
      {symbols
        .filter(symbol => assetFilter[symbol] || isCleanMode)
        .flatMap(symbol =>
          TIMEFRAME_LIST
            .filter(tf => timeframeFilter[tf.value] || isCleanMode)
            .map(tf => renderAssetSection(symbol, tf.value))
        )
      }
    </React.Fragment>
  );
};

export default PredictionsDashboard;
